import { SYSTEM_ACTOR_NAME } from '@supply/contracts'
import { Settings2 } from 'lucide-react'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import type { Member } from '@/data/types'
import { cn } from '@/lib/utils'

const SIZE = {
  sm: 'h-5 w-5 text-[9px]',
  md: 'h-7 w-7 text-[10px]',
} as const

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  const first = parts[0]?.[0] ?? '?'
  const last = parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : ''
  return `${first}${last}`.toUpperCase()
}

/**
 * Foto da pessoa, com as iniciais enquanto não carrega. O `title` fica sempre:
 * iniciais repetidas acontecem. Sem pessoa cadastrada (executor por texto),
 * mostra as iniciais do texto; sem ninguém, o "?" tracejado do Hub.
 */
export function MemberAvatar({
  member,
  text,
  size = 'sm',
  className,
}: {
  member?: Pick<Member, 'full_name' | 'avatar_url'> | undefined
  /** Executor de fora do cadastro (texto livre). */
  text?: string | null
  size?: keyof typeof SIZE
  className?: string
}) {
  const name = member?.full_name ?? text?.trim() ?? ''
  // Autor que é o dev admin fantasma (ADR 0008): sem iniciais nem foto.
  if (member && name === SYSTEM_ACTOR_NAME) {
    return (
      <span
        title={name}
        className={cn('inline-flex shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground', SIZE[size], className)}
      >
        <Settings2 className="h-3 w-3" aria-hidden />
        <span className="sr-only">{name}</span>
      </span>
    )
  }
  if (!name) {
    return (
      <span
        title="Sem executor"
        className={cn(
          'inline-flex shrink-0 items-center justify-center rounded-full border border-dashed border-border text-muted-foreground',
          SIZE[size],
          className,
        )}
      >
        ?
      </span>
    )
  }
  return (
    <Avatar title={name} className={cn('rounded-full', SIZE[size], className)}>
      {member?.avatar_url && <AvatarImage src={member.avatar_url} alt="" />}
      <AvatarFallback className={cn('rounded-full', !member && 'bg-muted text-muted-foreground', size === 'sm' ? 'text-[9px]' : 'text-[10px]')}>
        {initialsOf(name)}
      </AvatarFallback>
    </Avatar>
  )
}
